import React, { useEffect, useMemo, useState } from "react";
import Styles from "./MakerChecker.module.css";
import {
  getParentPaymentDetails,
  getParentPaymentDetailsHistory,
} from "../../../Services/GNA/GetData";
import { useSelector } from "react-redux";
import { ErrorMessage } from "../../../commons/Toast/ErrorMessage";
import { convertDate, formatDateType } from "../../../commons/FormatDate";
import Settlement from "../../Modals/Schedule/Settlement/Settlement";
import ViewDoc from "../../Modals/ConGen/ViewDoc/ViewDoc";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import SearchIcon from "@mui/icons-material/Search";

const MakerChecker = ({ type }) => {
  const role = useSelector((state) => state?.persistedReducer?.userType);
  const userId = useSelector(
    (state) => state?.persistedReducer?.user?.user?.userId,
  );
  const [settlement, setSettlement] = useState({ status: false, id: "", data: "" });
  const [data, setData] = useState(null);
  const [viewDocModalOpen, setViewDocModalOpen] = useState(false);
  const [viewDocConnectionId, setViewDocConnectionId] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(8);
  const [search, setSearch] = useState("");

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(
      (e) =>
        e?.bilateralContractUniqueId?.toLowerCase().includes(q) ||
        e?.selfCompanyName?.toLowerCase().includes(q) ||
        e?.counterPartyCompanyName?.toLowerCase().includes(q),
    );
  }, [data, search]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const paginatedData = useMemo(
    () => filteredData.slice(page * pageSize, page * pageSize + pageSize),
    [filteredData, page, pageSize],
  );

  useEffect(() => { setPage(0); }, [search]);

  useEffect(() => {
    if (type === "HISTORY") {
      handleHistory();
    } else {
      handleFetchData();
    }
  }, []);

  const handleFetchData = async () => {
    const res = await getParentPaymentDetails({ role, userId });
    if (res?.data?.code === 200) {
      setData(res?.data?.data?.page);
    } else {
      ErrorMessage(res?.data?.message || res?.data?.error || "Failed to fetch data");
    }
  };

  const handleHistory = async () => {
    const history = await getParentPaymentDetailsHistory({ role, userId });
    if (history?.data?.code === 200) {
      setData(history?.data?.data?.page || []);
    } else {
      ErrorMessage(history?.data?.message || history?.data?.error || "Failed to fetch history data");
    }
  };

  const getActivityLabel = (ele) => {
    if (role === "MAKER") {
      return ele?.status === "PAYMENT" || ele?.status === "CHECKER" ? "View" : "Settle";
    }
    if (role === "CHECKER") {
      return ele?.status === "PAYMENT" ? "View" : "Setlle";
    }
    return "View";
  };

  const getRateDisplay = (ele) => {
    if (ele?.type === "ONE_TIME") return "—";
    if (ele?.energyRateType === "FIXED") return ele?.energyRate?.toFixed(2);
    if (ele?.energyRateType === "DAM_PLUS") return `DAM+${ele?.energyRate}`;
    if (ele?.energyRateType === "DAM_MINUS") return `DAM-${ele?.energyRate}`;
    return "—";
  };

  const statusConfig = {
    PAYMENT: { cls: Styles.statusPayment, dot: "#34a863" },
    CHECKER: { cls: Styles.statusChecker, dot: "#4e79a7" },
    MAKER:   { cls: Styles.statusMaker,   dot: "#f0a417" },
    RECHECK: { cls: Styles.statusRecheck, dot: "#e74c3c" },
  };
  const getStatusCfg = (status) =>
    statusConfig[status] || { cls: Styles.statusDefault, dot: "#a9a8b1" };

  return (
    <>
      {settlement.status && (
        <Settlement
          open={settlement.status}
          handleClose={() => setSettlement({ status: false, id: "", data: "" })}
          id={settlement.id}
          data={settlement.data}
          admin={true}
          renderScheuleData={handleFetchData}
          type={type}
        />
      )}
      {viewDocModalOpen && (
        <ViewDoc
          open={viewDocModalOpen}
          handleClose={() => setViewDocModalOpen(false)}
          connectionId={viewDocConnectionId}
          viewDocData={data}
        />
      )}

      <div className={Styles.mkTable}>
        {/* Search bar */}
        <div className={Styles.mkSearchBar}>
          <div className={Styles.mkSearchBox}>
            <SearchIcon className={Styles.mkSearchIcon} />
            <input
              type="text"
              placeholder="Search by Contract ID, Buyer or Seller..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={Styles.mkSearchInput}
            />
          </div>
        </div>

        {/* Header */}
        <div className={Styles.mkHead}>
          <div className={Styles.colId}>Contract ID</div>
          <div className={Styles.colParty}>Buyer</div>
          <div className={Styles.colParty}>Seller</div>
          <div className={Styles.colFreq}>Charge Freq.</div>
          <div className={Styles.colDate}>Delivery Date</div>
          <div className={Styles.colVolume}>Volume (MWh)</div>
          <div className={Styles.colRate}>Rate (₹/kWh)</div>
          <div className={Styles.colStatus}>Status</div>
          <div className={Styles.colReceivedDate}>Received Date</div>
          <div className={Styles.colDocs}>Docs</div>
          <div className={Styles.colActivity}>Activity</div>
        </div>

        {/* Body */}
        <div className={Styles.mkBody}>
          {data === null ? (
            <div className={Styles.stateRow}>
              <HourglassTopIcon style={{ fontSize: "28px", opacity: 0.3 }} />
              <span>Loading…</span>
            </div>
          ) : data.length === 0 ? (
            <div className={Styles.stateRow}>
              <ReceiptLongIcon style={{ fontSize: "28px", opacity: 0.3 }} />
              <span>No activity found</span>
            </div>
          ) : (
            paginatedData.map((ele, i) => (
              <div className={Styles.mkRow} key={ele?.settlementId || i}>
                <div className={`${Styles.colId} ${Styles.contractId}`}>
                  {ele?.bilateralContractUniqueId || "—"}
                </div>
                <div className={`${Styles.colParty} ${Styles.truncate}`}>
                  {ele?.selfCompanyName || "—"}
                </div>
                <div className={`${Styles.colParty} ${Styles.truncate}`}>
                  {ele?.counterPartyCompanyName || "—"}
                </div>
                <div className={Styles.colFreq}>
                  <span className={Styles.freqBadge}>{ele?.type || "—"}</span>
                </div>
                <div className={Styles.colDate}>
                  {ele?.fromDate ? convertDate(ele.fromDate) : "—"}
                </div>
                <div className={Styles.colVolume}>
                  {ele?.deliveredVolume != null ? ele.deliveredVolume.toFixed(2) : "—"}
                </div>
                <div className={Styles.colRate}>{getRateDisplay(ele)}</div>
                <div className={Styles.colStatus}>
                  {(() => { const cfg = getStatusCfg(ele?.status); return (
                    <span className={`${Styles.statusBadge} ${cfg.cls}`}>
                      <span className={Styles.statusDot} style={{ background: cfg.dot }} />
                      {ele?.status || "—"}
                    </span>
                  ); })()}
                </div>
                <div className={Styles.colReceivedDate}>
                  {ele?.createdAt ? formatDateType(ele?.createdAt) : "—"}
                </div>
                <div className={Styles.colDocs}>
                  <button
                    className={Styles.iconBtn}
                    title="View documents"
                    onClick={() =>
                      setViewDocModalOpen(true) ||
                      setViewDocConnectionId(ele?.bilateralContractId)
                    }
                  >
                    <VisibilityIcon style={{ fontSize: "16px" }} />
                  </button>
                </div>
                <div className={Styles.colActivity}>
                  <button
                    className={Styles.activityBtn}
                    onClick={() =>
                      setSettlement({ status: true, id: ele?.settlementId, data: ele })
                    }
                  >
                    {getActivityLabel(ele)} Transaction
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {filteredData.length > 0 && (
          <div className={Styles.pagination}>
            <span className={Styles.pageInfo}>
              {page * pageSize + 1}–{Math.min((page + 1) * pageSize, filteredData.length)} of {filteredData.length}
              {search && data && filteredData.length !== data.length && ` (filtered from ${data.length})`}
            </span>
            <div className={Styles.pageControls}>
              <select
                className={Styles.pageSizeSelect}
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
              >
                {[5, 8, 10, 20].map((n) => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>
              <div className={Styles.pageButtons}>
                <button
                  className={Styles.pageBtn}
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >‹</button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pageNum = Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
                  return (
                    <button
                      key={pageNum}
                      className={`${Styles.pageBtn} ${pageNum === page ? Styles.pageBtnActive : ""}`}
                      onClick={() => setPage(pageNum)}
                    >{pageNum + 1}</button>
                  );
                })}
                <button
                  className={Styles.pageBtn}
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >›</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MakerChecker;
